import { useEffect, useState } from 'react'

/**
 * Vertical three-dot trail
 */
const FRAME_MS = 480
/** Per frame: which dot (0–2) is emphasized, or null = all muted. Length = cycle length. */
const EMPHASIZED_DOT_BY_FRAME: (0 | 1 | 2 | null)[] = [null, 0, 1, 2]
const FRAME_COUNT = EMPHASIZED_DOT_BY_FRAME.length

function useDotLoaderFrame(isActive: boolean) {
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    if (!isActive) {
      setFrame(0)
      return
    }
    const intervalId = window.setInterval(() => {
      setFrame((f) => (f + 1) % FRAME_COUNT)
    }, FRAME_MS)
    return () => window.clearInterval(intervalId)
  }, [isActive])

  return frame
}

type DotLoaderProps = {
  /** When true, animated dot trail; when false, static vertical pill (idle). */
  loading: boolean
}

/** Frame 0 = all muted; frames 1–3 emphasize top → middle → bottom. Fixed height so dots don’t shift layout. */
export function DotLoader({ loading }: DotLoaderProps) {
  const frame = useDotLoaderFrame(loading)

  if (!loading) {
    return (
      <div className="flex h-[35px] w-full shrink-0 items-center justify-center" aria-hidden>
        <div className="h-[31px] w-0.5 shrink-0 rounded-[16px] bg-brand-neutral-300" />
      </div>
    )
  }

  const emphasizedIndex = EMPHASIZED_DOT_BY_FRAME[frame] ?? null

  return (
    <div className="flex h-[35px] shrink-0 flex-col items-center gap-[7px]" aria-hidden>
      {([0, 1, 2] as const).map((i) => {
        const emphasized = emphasizedIndex === i
        return (
          <div key={i} className="flex h-[7px] w-[7px] items-center justify-center">
            {/* Each dot is 5px x 5px, and 7px x 7px when emphasized */}
            <div
              className={
                emphasized
                  ? 'h-[7px] w-[7px] rounded-full bg-brand-500 transition-all duration-200'
                  : 'h-[5px] w-[5px] rounded-full bg-brand-200 transition-all duration-200'
              }
            />
          </div>
        )
      })}
    </div>
  )
}
